import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CharacterService } from "../../../services/characterService";
import { CampaignAssetService } from "../../../services/campaignAssetService";
import { CampaignAsset, CharacterListItem } from "../../../types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, useToast } from "../../../components/ui";
import { TreeView, TreeNodeData } from "../../../components/ui/TreeView";
import { AssetCard, DmNotesField } from "../../../components/business";
import { faSpinner, faEye, faEdit } from "@fortawesome/free-solid-svg-icons";
import type { AssetAction } from "../../../components/business";
import CustomAssetReferenceEditor from "@/components/rich-text-editor";

interface CampaignWorldProps {
  campaignId: number;
}

const CampaignWorld: React.FC<CampaignWorldProps> = ({ campaignId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [campaignAssets, setCampaignAssets] = useState<CampaignAsset[]>([]);
  const [characters, setCharacters] = useState<CharacterListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedAssets, setExpandedAssets] = useState<Set<string | number>>(new Set());
  const [selectedAsset, setSelectedAsset] = useState<CampaignAsset | null>(null);

  // Search state for filtering world assets tree
  const [search, setSearch] = useState("");
  const [filteredAssets, setFilteredAssets] = useState<TreeNodeData[] | null>(null);
  // Recursive search for top-level matches by name, description, or category (TreeNodeData for campaign assets)
  function findTopLevelWorldMatches(tree: TreeNodeData[], search: string): TreeNodeData[] {
    let matches: TreeNodeData[] = [];
    const lowerSearch = search.toLowerCase();
    for (const node of tree) {
      const asset = node.data as CampaignAsset;
      const nameMatch = asset.asset.name.toLowerCase().includes(lowerSearch);
      const descMatch =
        asset.asset.description && asset.asset.description.toLowerCase().includes(lowerSearch);
      const categoryMatch =
        asset.asset.category && asset.asset.category.toLowerCase().includes(lowerSearch);
      if (nameMatch || descMatch || categoryMatch) {
        matches.push(node);
      } else if (node.children && node.children.length > 0) {
        matches = matches.concat(findTopLevelWorldMatches(node.children, search));
      }
    }
    return matches;
  }

  useEffect(() => {
    if (!search) {
      setFilteredAssets(null);
      return;
    }
    const sharedAssets = getSharedAssets();
    const hierarchicalAssets = buildAssetHierarchy(sharedAssets);
    const found = findTopLevelWorldMatches(hierarchicalAssets, search);
    setFilteredAssets(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, campaignAssets]);

  // Create world-specific asset actions (read-only in world view)
  const handleUpdateCampaignAssetDmNotes = async (dmNotes: string) => {
    if (!selectedAsset) return;
    try {
      const response = await fetch(
        `/api/protected/campaigns/${campaignId}/assets/${selectedAsset.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ dmNotes }),
        }
      );
      if (response.ok) {
        setSelectedAsset((prev) => (prev ? { ...prev, dmNotes } : prev));
      } else {
        showToast({ type: "error", message: "Failed to save DM notes." });
      }
    } catch {
      showToast({ type: "error", message: "Failed to save DM notes." });
    }
  };

  const handleUpdateCampaignSectionDmNotes = async (sectionId: number, dmNotes: string) => {
    if (!selectedAsset) return;
    const section = selectedAsset.sections.find((s) => s.id === sectionId);
    if (!section) return;
    try {
      const response = await fetch(
        `/api/protected/campaigns/${campaignId}/assets/${selectedAsset.id}/sections/${sectionId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: section.name, content: section.content, dmNotes }),
        }
      );
      if (response.ok) {
        setSelectedAsset((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            sections: prev.sections.map((s) => (s.id === sectionId ? { ...s, dmNotes } : s)),
          };
        });
      } else {
        showToast({ type: "error", message: "Failed to save section DM notes." });
      }
    } catch {
      showToast({ type: "error", message: "Failed to save section DM notes." });
    }
  };

  const createWorldAssetActions = (asset: CampaignAsset): AssetAction[] => [
    {
      label: "View Details",
      icon: faEye,
      onClick: () => setSelectedAsset(asset),
      variant: "primary",
    },
    {
      label: "View in Campaign",
      icon: faEdit,
      onClick: () => navigate(`/protected/campaigns/${campaignId}/assets/${asset.id}`),
      variant: "secondary",
    },
  ];

  // Convert CampaignAsset to TreeNodeData format
  const convertCampaignAssetToTreeNode = (
    asset: CampaignAsset & { children?: CampaignAsset[] }
  ): TreeNodeData => ({
    id: asset.id,
    label: asset.asset.name,
    children: asset.children?.map(convertCampaignAssetToTreeNode),
    data: asset,
  });

  // Custom render function for campaign asset nodes using AssetCard
  const renderCampaignAssetNode = (node: TreeNodeData, level: number) => {
    const asset = node.data as CampaignAsset & { children?: CampaignAsset[] };
    const hasChildren = asset.children && asset.children.length > 0;
    // Convert CampaignAsset to AssetTableItem format for AssetCard
    const assetForCard = asset;

    return (
      <AssetCard
        key={asset.id}
        asset={assetForCard}
        variant="tree"
        level={level}
        hasChildren={hasChildren}
        isExpanded={expandedAssets.has(node.id)}
        onToggleExpand={() => handleToggleExpand(node.id)}
        hideExpandButton // Hide the AssetCard's expand button since TreeView handles it
        // showActions
        actions={createWorldAssetActions(asset)}
        onClick={() => setSelectedAsset(asset)}
        className="mb-2 cursor-pointer"
      />
    );
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [assetsData, charactersData] = await Promise.all([
        CampaignAssetService.getSharedCampaignAssets(campaignId),
        CharacterService.getCharacters(),
      ]);

      setCampaignAssets(assetsData);
      setCharacters(charactersData);
    } catch {
      showToast({ message: "Failed to load world assets", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Get current user's character IDs to determine asset ownership
  const getCurrentUserCharacterIds = (): number[] => {
    if (!characters.length) return [];

    // We need to determine current user's characters
    // For now, we'll return all character IDs since we don't have direct user ID
    // In a real implementation, you'd filter by userId === currentUser.id
    return characters.map((char) => char.id);
  };

  // Filter shared assets (assets that have access granted to characters other than owner)
  const getSharedAssets = (): CampaignAsset[] => {
    const userCharacterIds = getCurrentUserCharacterIds();

    return campaignAssets.filter((asset) => {
      // Show assets that have been shared (have access users) OR
      // assets owned by current user that they've shared with others
      const hasSharedAccess = asset.accessUsers && asset.accessUsers.length > 0;
      const hasUserAccess = asset.accessUsers?.some((access) =>
        userCharacterIds.includes(access.characterId)
      );

      return hasSharedAccess && (hasUserAccess || asset.asset.userId);
    });
  };

  // Build hierarchy for shared assets and convert to TreeView format
  const buildAssetHierarchy = (assets: CampaignAsset[]): TreeNodeData[] => {
    const assetMap = new Map<number, CampaignAsset & { children: CampaignAsset[] }>();
    const rootAssets: (CampaignAsset & { children: CampaignAsset[] })[] = [];

    // First pass: create map and add children property
    assets.forEach((asset) => {
      assetMap.set(asset.asset.id, { ...asset, children: [] });
    });

    // Second pass: build hierarchy based on parentId
    assets.forEach((asset) => {
      const assetWithChildren = assetMap.get(asset.asset.id)!;

      if (asset.asset.parentId) {
        const parent = assetMap.get(asset.asset.parentId);
        if (parent) {
          parent.children.push(assetWithChildren);
        } else {
          // Parent not in shared assets, treat as root
          rootAssets.push(assetWithChildren);
        }
      } else {
        rootAssets.push(assetWithChildren);
      }
    });

    return rootAssets.map(convertCampaignAssetToTreeNode);
  };

  const handleToggleExpand = (nodeId: string | number) => {
    setExpandedAssets((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const handleSelectAsset = (node: TreeNodeData) => {
    const asset = node.data as CampaignAsset;
    setSelectedAsset(asset);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8 bg-gray-800 rounded-lg">
        <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-yellow-400" />
        <span className="ml-2 text-white">Loading world assets...</span>
      </div>
    );
  }

  const sharedAssets = getSharedAssets();
  const hierarchicalAssets = buildAssetHierarchy(sharedAssets);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold text-white">World Assets</h3>
          <p className="text-gray-400 text-sm mt-1">
            Assets shared within this campaign - view-only with full sections
          </p>
        </div>
      </div>

      {/* Search input for world assets */}
      <div className="mb-3 flex items-center gap-2">
        <input
          type="text"
          className="p-2 rounded bg-gray-700 text-white border border-gray-600 w-full"
          placeholder="Search world asset name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Shared Assets List */}
      {sharedAssets.length === 0 ? (
        <div className="text-center py-8 bg-gray-800 rounded-lg">
          <p className="text-gray-400">No shared assets in this campaign yet.</p>
          <p className="text-gray-500 text-sm mt-2">
            Assets will appear here when they are shared with campaign characters.
          </p>
        </div>
      ) : (
        <TreeView
          nodes={filteredAssets !== null ? filteredAssets : hierarchicalAssets}
          selectedId={selectedAsset?.id}
          expandedIds={expandedAssets}
          onSelect={handleSelectAsset}
          onToggleExpand={handleToggleExpand}
          renderNode={renderCampaignAssetNode}
          showConnectors={true}
        />
      )}

      {/* Asset Details Modal */}
      {selectedAsset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto border border-gray-700">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-white">{selectedAsset.asset.name}</h3>
              <Button
                onClick={() => setSelectedAsset(null)}
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-gray-300"
              >
                ✕
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-gray-400 text-sm">
                  {selectedAsset.asset.type} • {selectedAsset.asset.category}
                </p>
                {selectedAsset.asset.description && (
                  <p className="text-gray-300 mt-2">
                    <CustomAssetReferenceEditor
                      value={selectedAsset.asset.description}
                      readOnly
                      hideTooltip
                      campaignId={campaignId}
                    />
                  </p>
                )}
                <div className="mt-3">
                  <DmNotesField
                    value={selectedAsset.dmNotes ?? ""}
                    onSave={handleUpdateCampaignAssetDmNotes}
                  />
                </div>
              </div>

              {selectedAsset.sections && selectedAsset.sections.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-2">Revealed Folios:</h4>
                  <div className="space-y-3">
                    {selectedAsset.sections.map((section) => (
                      <div key={section.id} className="bg-gray-700 rounded p-4">
                        <h5 className="font-medium text-white mb-2">{section.name}</h5>
                        <p className="text-gray-300 whitespace-pre-wrap">
                          <CustomAssetReferenceEditor
                            value={section.content}
                            readOnly
                            hideTooltip
                            campaignId={campaignId}
                          />
                        </p>
                        <div className="mt-3">
                          <DmNotesField
                            value={section.dmNotes ?? ""}
                            onSave={(dmNotes) =>
                              handleUpdateCampaignSectionDmNotes(section.id, dmNotes)
                            }
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignWorld;
